import './_styles.scss';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Grid, Button } from 'react-bootstrap';
import { has, isEqual, pickBy } from 'lodash';
import qs from 'qs';

import FilterGroup from '../../components/Filters/FilterGroup';
import SearchGroup from '../../components/Search/SearchGroup';
import SearchButton from '../../components/Search/SearchButton';
import Pagination from '../../components/Pagination';
import Spinner from '../../components/Loaders/Spinner';
import Table from './components/Table';
import ModalsManager from './components/Modals/Manager';
import Loader from '../../components/Loaders/Loader';

import {
  getPaymentsList,
  getPaymentsListPagination,
  getPaymentsConfig,
  getPaymentsMethods,
  getPaymentsCurrentMethods,
  getPaymentsStatuses,
  getPaymentsFraudRisk,
  getPaymentsModes,
  getPaymentDetails,
  getPaymentSuperDetails,
  addPayment,
  editPayment,
  completePayment,
  sendFraudReport,
} from '../../services/endpoints/payments';
import { getNavbarCountersAction } from '../../redux/actions/header';
import { WebsocketContext } from '../../utils/WebsocketProvider';

const Payments = () => {
  const { eventData } = useContext(WebsocketContext);

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const queryString = qs.parse(location.search.slice(1));

  const selectedItem = useRef({});

  const [init, setInit] = useState(false);
  const [data, setData] = useState({});
  const [config, setConfig] = useState({});
  const [isLoading, setIsLoading] = useState({ page: true, pagination: true, modal: false, modalSubmit: false, table: false });
  const [openModal, setOpenModal] = useState('');
  const [modalError, setModalError] = useState('');
  const [details, setDetails] = useState({});

  const onHideModals = () => {
    setOpenModal('');
    setIsLoading((prevState) => ({ ...prevState, modal: false }));
    setTimeout(() => {
      setDetails({});
      selectedItem.current = {};
      setModalError('');
    }, 150);
  };

  const fetchList = async (scrollTop) => {
    setIsLoading((prevState) => ({ ...prevState, table: true }));

    try {
      const response = await getPaymentsList(queryString);
      const isUpdatePagination = !isEqual(data?.filters, response?.data?.filters)
      if (response.success) {
        setData((prevState) => ({
          ...response.data,
          pagination: isUpdatePagination ? response.data.pagination : {
            ...prevState.pagination,
            current_page: response.data.pagination.current_page,
          },
        }));

        if (scrollTop) {
          const pageWrapper = document.querySelector('#page-wrapper');
          setTimeout(() => {
            pageWrapper?.scrollTo(0, scrollTop);
          }, 0);
        }
      }
      setIsLoading((prevState) => ({ ...prevState, table: false }));
      return {
        ...response.data,
        need_pagination: isUpdatePagination,
      };
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  };

  const fetchListPagination = async () => {
    setIsLoading((prevState) => ({ ...prevState, pagination: true }));

    try {
      const response = await getPaymentsListPagination(queryString);
      if (response.success) {
        setData((prevState) => ({ ...prevState, pagination: response.data.pagination }));
      }
      setIsLoading((prevState) => ({ ...prevState, pagination: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, pagination: false }));
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await getPaymentsConfig(queryString);
      if (response.success) {
        setConfig(response.data);
        return response.data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAllData = async () => {
    setIsLoading((prevState) => ({ ...prevState, page: true }));

    try {
      const configResponse = !init ? await fetchConfig() : config;
      if (configResponse?.access?.list) {
        const listResponse = await fetchList();
        setIsLoading((prevState) => ({ ...prevState, page: false }));
        if (listResponse?.need_pagination) {
          await fetchListPagination();
        } else {
          setIsLoading((prevState) => ({ ...prevState, pagination: false }));
        }
      } else {
        setIsLoading((prevState) => ({ ...prevState, page: false }));
      }
      setInit(true);
    } catch (error) {
      setInit(true);
      setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  };

  const fetchCurrentMethods = async () => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));

    try {
      const response = await getPaymentsCurrentMethods();
      if (response.success) {
        setConfig((prevState) => ({ ...prevState, current_methods: response.data }));
      }
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const fetchTableFilters = async (name) => {
    let request = null;

    switch (name) {
    case 'methods':
      request = getPaymentsMethods;
      break;

    case 'statuses':
      request = getPaymentsStatuses;
      break;

    case 'fraud_risk':
      request = getPaymentsFraudRisk;
      break;

    case 'modes':
      request = getPaymentsModes;
      break;

    default:
      break;
    }

    try {
      const response = await request(queryString);

      if (response?.success) {
        setConfig((prevState) => ({ ...prevState, [name]: response?.data?.[name] }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPaymentDetails = async (id) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));

    try {
      const response = await getPaymentDetails(id);

      if (response.success) {
        setDetails(response?.data);
      }
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const fetchPaymentSuperDetails = async (id) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));

    try {
      const response = await getPaymentSuperDetails(id);

      if (response.success) {
        setDetails(response?.data);
        setIsLoading((prevState) => ({ ...prevState, modal: false }));
      }
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const onAddPayment = async (payload, scrollTop) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));

    try {
      await addPayment({ ...payload, method: Number(payload.method) });
      onHideModals();
      config?.access?.list && fetchList(scrollTop);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  };

  const onEditPayment = async (payload, scrollTop) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));

    try {
      await editPayment(selectedItem.current?.id, { ...payload, method: Number(payload.method) });
      onHideModals();
      config?.access?.list && fetchList(scrollTop);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  };

  const onReportFraud = async (payload, scrollTop) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));

    try {
      await sendFraudReport(selectedItem.current?.id, payload);
      onHideModals();
      config?.access?.list && fetchList(scrollTop);
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const onRejectConfirm = async (payload, scrollTop) => {
    onHideModals();
    config?.access?.list && fetchList(scrollTop);
  };

  const onCompletePayment = async (payload, scrollTop) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));

    try {
      await completePayment(selectedItem.current?.id, payload);
      onHideModals();
      config?.access?.list && fetchList(scrollTop);
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const onSortTable = (head) => {
    if (head.active === 'desc') {
      delete queryString.page;
      queryString.sort = head.asc;
    }
    if (head.active === 'asc' || !head.active) {
      delete queryString.page;
      queryString.sort = head.desc;
    }
    return history.push(`${location.pathname}?${(location.search = qs.stringify(queryString))}`);
  };

  const onOpenAddEdit = (type) => {
    fetchCurrentMethods();
    setOpenModal(type);
  };

  const onSelectAction = (actionId, title, item) => {
    switch (actionId) {
    case 0:
    case 1:
      selectedItem.current = {
        ...item,
        type: actionId === 0 ? 'accept' : 'complete',
      };
      setOpenModal('complete');
      break;

    case 2:
    case 3:
      actionId === 2 ? fetchPaymentDetails(item.id) : actionId === 3 ? fetchPaymentSuperDetails(item.id) : () => {};
      selectedItem.current = {
        ...item,
        type: actionId === 2 ? 'details' : actionId === 3 ? 'superdetails' : null,
      };
      setOpenModal('details');
      break;

    case 4:
      selectedItem.current = item;
      onOpenAddEdit('edit');
      break;

    case 5:
      selectedItem.current = item;
      setOpenModal('report');
      break;

      // Reject
    case 6:
      selectedItem.current = item;
      setOpenModal('reject');
      break;

    default:
      break;
    }
  };

  const onSubmitModal = (data, actionType) => {
    const scrollTop = document.querySelector('#page-wrapper')?.scrollTop;

    switch (actionType) {
    case 'add':
      onAddPayment(data, scrollTop);
      break;

    case 'edit':
      onEditPayment(data, scrollTop);
      break;

    case 'complete':
      onCompletePayment(data, scrollTop);
      break;

    case 'report':
      onReportFraud(data, scrollTop);
      break;

    case 'reject':
      onRejectConfirm(data, scrollTop)
      break;

    default:
      break;
    }
  };

  const onSelectMethods = (options) => {
    if (options?.length === 0 && !has(queryString, 'method')) return;
    const newOptions = options.filter((option) => option.id !== null).map((opt) => opt.id);
    const queryObj = {
      ...queryString,
      method: newOptions,
    };
    if (has(queryObj, 'page')) delete queryObj.page;
    history.push({ pathname: location.pathname, search: qs.stringify({ ...pickBy(queryObj) }) });
  };

  useEffect(() => {
    getAllData();
    document.title = 'Payments';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(
    () => {
      dispatch(getNavbarCountersAction({ code: 'payments-list' }));
      return () => {

      }
    },
    [dispatch],
  );

  useEffect(() => {
    switch (eventData?.name) {
    case 'menu_counter_update':
      if (eventData?.options?.code === 'payments-list') {
        getAllData();
      }
      break;

    default:
      break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  return (
    <Grid fluid className="payments">
      <ModalsManager
        open={openModal}
        item={selectedItem?.current}
        details={details}
        error={modalError}
        isLoading={isLoading.modal}
        isLoadingSubmit={isLoading.modalSubmit}
        methods={config?.current_methods}
        excludedMethods={config?.affiliate_excluded_methods}
        onHide={onHideModals}
        onSubmit={onSubmitModal}
      />

      <FilterGroup>
        {config?.access?.add && <Button onClick={() => onOpenAddEdit('add')}>Add payment</Button>}
        {config?.access?.list && (
          <SearchGroup
            autocomplete="on"
            sectionName="payments"
            placeholder="Search"
            withSelect
            options={config?.search_types}
            filters={data?.filters}
            disabled={isLoading.page}
          />
        )}
        {config?.access?.export_list && <SearchButton onClickButton={() => history.push('/payments/export')} buttonText="Export" />}
      </FilterGroup>

      {isLoading.page && !init ? (
        <Loader />
      ) : (
        <div className="loader-container" style={{ minHeight: '600px' }}>
          {config?.access?.list ? (
            <>
              <Table
                items={data?.payments}
                config={config}
                sort={data?.sort}
                filters={data?.filters}
                isLoading={isLoading.table}
                fetchTableFilters={fetchTableFilters}
                onSortTable={onSortTable}
                onSelectAction={onSelectAction}
                onSelectMethods={onSelectMethods}
              />

              {isLoading.pagination || isLoading.table ? (
                <div className="pagination">
                  <Spinner />
                </div>
              ) : (
                <Pagination
                  pages={data.pagination?.pages}
                  currentPage={data.pagination?.current_page}
                  itemsCount={data.pagination?.total_count}
                  pageSize={data.pagination?.page_size}
                  pagesLimit={data.pagination?.pages < 10 ? data.pagination?.pages : 10}
                />
              )}
            </>
          ) : null}
        </div>
      )}
    </Grid>
  );
};

export default Payments;
